import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { NAV } from "@/constants/testIds";

const NAV_ITEMS = [
  { to: "/", label: "Home", tid: NAV.home },
  { to: "/products", label: "Products", tid: NAV.products },
  { to: "/process", label: "Process", tid: NAV.process },
  { to: "/resources", label: "Resources", tid: NAV.resources },
  { to: "/blog", label: "Blog", tid: NAV.blog },
  { to: "/deal-structures", label: "Deal Structures", tid: NAV.dealStructures },
  { to: "/faq", label: "FAQ", tid: NAV.faq },
  { to: "/about", label: "About", tid: NAV.about },
  { to: "/contact", label: "Contact", tid: NAV.contact },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[color:var(--bg)]/85 backdrop-blur-xl border-b border-black/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid={NAV.logo} aria-label="Steady Path Funding home">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.tid}
              className={({ isActive }) =>
                `text-[13px] tracking-wide font-mono uppercase transition-colors ${
                  isActive
                    ? "text-[color:var(--gold)]"
                    : "text-[color:var(--bone)]/70 hover:text-[color:var(--bone)]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/apply"
            data-testid={NAV.applyButton}
            className="group hidden sm:inline-flex items-center gap-2 border border-[color:var(--gold)] text-[color:var(--gold)] hover:bg-[color:var(--gold)] hover:text-black transition-colors duration-300 px-5 py-2.5 text-xs font-mono tracking-[0.2em] uppercase"
          >
            Apply
            <ArrowUpRight
              size={14}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Link>
          <button
            data-testid={NAV.mobileMenuToggle}
            aria-label="Toggle menu"
            className="lg:hidden text-[color:var(--bone)] p-2"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.7, 0, 0.2, 1] }}
            className="lg:hidden overflow-hidden bg-[color:var(--bg)] border-t border-black/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `font-serif text-2xl transition-colors ${
                      isActive
                        ? "text-[color:var(--gold)]"
                        : "text-[color:var(--bone)]"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/apply"
                className="mt-4 inline-flex items-center justify-center gap-2 bg-[#3d3529] text-white px-5 py-3 font-mono text-xs tracking-[0.2em] uppercase"
              >
                Apply
                <ArrowUpRight size={14} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
